/**
 * Safari Menu Drawer Fix
 *
 * This script addresses the issue with Safari mobile's bottom toolbar
 * overlapping the menu drawer's social media icons when scrolling up.
 *
 * It works by:
 * 1. Detecting iOS Safari
 * 2. Setting CSS variables for viewport height and safe area
 * 3. Dynamically positioning the social media icons based on toolbar visibility
 * 4. Handling scroll events to detect toolbar visibility changes
 */

(function() {
  // Detect iOS Safari
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Only apply fixes on iOS or Safari
  if (!(isIOS || isSafari)) return;

  // Track toolbar visibility state
  let isToolbarVisible = false;
  let lastScrollY = 0;
  let lastWindowHeight = window.innerHeight;

  // Set the viewport height and safe area CSS variables
  function updateSafariVariables() {
    // Get the viewport height and set the --vh variable
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // Set the Safari toolbar height variable
    document.documentElement.style.setProperty('--safari-toolbar-height', '75px');
  }

  // Set initial values
  updateSafariVariables();

  // Update variables on window resize and orientation change
  window.addEventListener('resize', updateSafariVariables);
  window.addEventListener('orientationchange', updateSafariVariables);

  // Apply fixes when the DOM is loaded
  document.addEventListener('DOMContentLoaded', function() {
    const menuDrawer = document.getElementById('menu-drawer');
    if (!menuDrawer) return;

    // Detect Safari toolbar visibility
    function detectToolbarVisibility() {
      // If the window height changes significantly, the toolbar likely appeared/disappeared
      const currentWindowHeight = window.innerHeight;
      const heightDifference = Math.abs(currentWindowHeight - lastWindowHeight);

      // If height changed by more than 40px, toolbar state likely changed
      if (heightDifference > 40) {
        // If current height is less than previous, toolbar appeared
        isToolbarVisible = currentWindowHeight < lastWindowHeight;
        lastWindowHeight = currentWindowHeight;

        // Update utility links position based on toolbar visibility
        updateUtilityLinksPosition();
      }

      // Also detect based on scroll direction
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY + 10) {
        // Scrolling down - toolbar likely hidden
        if (isToolbarVisible) {
          isToolbarVisible = false;
          updateUtilityLinksPosition();
        }
      } else if (currentScrollY < lastScrollY - 10) {
        // Scrolling up - toolbar likely visible
        if (!isToolbarVisible) {
          isToolbarVisible = true;
          updateUtilityLinksPosition();
        }
      }
      lastScrollY = currentScrollY;
    }

    // Update utility links position based on toolbar visibility
    function updateUtilityLinksPosition() {
      const utilityLinks = menuDrawer.querySelector('.menu-drawer__utility-links');
      if (!utilityLinks) return;

      if (isIOS || isSafari) {
        // Position based on toolbar visibility
        if (isToolbarVisible) {
          // Toolbar is visible - position higher
          utilityLinks.style.bottom = 'calc(var(--safe-area-bottom) + var(--safari-toolbar-height))';
        } else {
          // Toolbar is hidden - position lower
          utilityLinks.style.bottom = 'var(--safe-area-bottom)';
        }

        // Common styles
        utilityLinks.style.position = 'fixed';
        utilityLinks.style.left = '0';
        utilityLinks.style.right = '0';
        utilityLinks.style.width = '100%';
        utilityLinks.style.zIndex = '9999';
        utilityLinks.style.backgroundColor = 'var(--color-primary-300)';
        utilityLinks.style.padding = '16px';
        utilityLinks.style.transition = 'bottom 0.3s ease';

        // Style the social media links
        const socialLinks = utilityLinks.querySelector('.container-social-media-links');
        if (socialLinks) {
          socialLinks.style.display = 'flex';
          socialLinks.style.justifyContent = 'center';
          socialLinks.style.gap = '16px';
        }
      }
    }

    // Create a dynamic safe area at the bottom of the menu drawer
    function createSafeArea() {
      // Remove any existing safe area
      const existingSafeArea = menuDrawer.querySelector('.safari-bottom-safe-area');
      if (existingSafeArea) {
        existingSafeArea.remove();
      }

      // Create a new safe area
      const safeArea = document.createElement('div');
      safeArea.className = 'safari-bottom-safe-area';
      safeArea.style.width = '100%';
      safeArea.style.position = 'fixed';
      safeArea.style.bottom = '0';
      safeArea.style.left = '0';
      safeArea.style.right = '0';
      safeArea.style.backgroundColor = 'var(--color-primary-300)';
      safeArea.style.zIndex = '999';
      safeArea.style.pointerEvents = 'none';
      safeArea.style.transition = 'height 0.3s ease';

      // Set height based on toolbar visibility
      updateSafeAreaHeight(safeArea);

      menuDrawer.appendChild(safeArea);
      return safeArea;
    }

    // Update safe area height based on toolbar visibility
    function updateSafeAreaHeight(safeArea) {
      if (!safeArea) return;

      if (isToolbarVisible) {
        safeArea.style.height = 'calc(var(--safe-area-bottom) + var(--safari-toolbar-height))';
      } else {
        safeArea.style.height = 'var(--safe-area-bottom)';
      }
    }

    // Apply all fixes to the menu drawer
    function applyAllFixes() {
      // Add padding to the inner container
      const innerContainer = menuDrawer.querySelector('.menu-drawer__inner-container');
      if (innerContainer) {
        innerContainer.style.paddingBottom = 'calc(var(--safari-toolbar-height) + var(--safe-area-bottom) + 20px)';
      }

      // Add padding to the navigation area
      const navigation = menuDrawer.querySelector('.menu-drawer__navigation');
      if (navigation) {
        navigation.style.paddingBottom = 'calc(var(--safari-toolbar-height) + 20px)';

        // Handle scroll events within the navigation
        navigation.addEventListener('scroll', function() {
          // When scrolling near the bottom, ensure the utility links are visible
          const isNearBottom = this.scrollHeight - this.scrollTop - this.clientHeight < 100;

          if (isNearBottom) {
            // Make sure the utility links are visible
            const utilityLinks = menuDrawer.querySelector('.menu-drawer__utility-links');
            if (utilityLinks) {
              utilityLinks.style.opacity = '1';
            }
          }

          // Prevent scrolling to the very top which can trigger Safari's toolbar
          if (this.scrollTop <= 1) {
            this.scrollTop = 1;
          }
        });
      }

      // Create the safe area
      const safeArea = createSafeArea();

      // Set initial toolbar visibility state
      detectToolbarVisibility();

      // Update utility links position
      updateUtilityLinksPosition();

      // Listen for scroll events to detect toolbar visibility changes
      window.addEventListener('scroll', function() {
        detectToolbarVisibility();
        if (safeArea) updateSafeAreaHeight(safeArea);
      });
    }

    // Apply fixes when the menu is opened
    const menuToggle = document.querySelector('.header__icon--menu');
    if (menuToggle) {
      menuToggle.addEventListener('click', function() {
        // Small delay to ensure the drawer is visible
        setTimeout(applyAllFixes, 50);
      });
    }

    // Apply fixes immediately
    applyAllFixes();

    // Update on orientation change
    window.addEventListener('orientationchange', function() {
      setTimeout(function() {
        detectToolbarVisibility();
        updateUtilityLinksPosition();
      }, 100);
    });

    // Update on resize
    window.addEventListener('resize', function() {
      detectToolbarVisibility();
    });
  });
})();
